import { all, select, put } from 'redux-saga/effects';
import {
  PapiDaSharedGallectionSearch,
  PapiRequestDaSharedGallectionSearch,
} from '@wix/da-papi-types';
import { normalize } from '@wix/da-shared-react/pkg/redux/normalizr';
import { deviationSchema } from '@wix/da-shared-react/pkg/redux/normalizr/schemas/deviation';
import { withOffset, selectors as streamSelectors } from '@wix/da-shared-react/pkg/Stream';
import { fetchStreamContents } from '@wix/da-shared-react/pkg/Stream/helpers';
import { FetchType } from '@wix/da-shared-react/pkg/Stream/base/types';
import { getProfileOwner } from '../../selectors/users';
import { validateGallectionSearchRequest } from '@wix/da-shared-react/pkg/utils/validation/validateGallectionSearchRequest';
import { putErrorNotification } from '@wix/da-shared-react/pkg/utils/saga';
import i18next from 'i18next';

import { GallectionSortOrder } from '../../../types/gallection';
import { fetchNextGallectionSearchResults } from '../../actions/gallectionSection';

interface GallectionSearchStreamParams {
  gallectionType?: 'gallery' | 'collection' | 'private_collection';
  mode: 'paged' | 'scroll';
  searchQuery: string;
  sortOrder: GallectionSortOrder;
}

export default function* fetchMoreGallectionSearchResults({
  payload: { streamId },
}: ReturnType<typeof fetchNextGallectionSearchResults>) {
  const [offset, limit, profileOwner, streamParams] = yield all([
    select(state => withOffset.selectors.getNextOffset(state, streamId)),
    select(state => streamSelectors.getItemsPerFetch(state, streamId)),
    select(getProfileOwner),
    select(state =>
      streamSelectors.getStreamParamsSafe<GallectionSearchStreamParams>(state, streamId)
    ),
  ]);

  const queryParams: PapiRequestDaSharedGallectionSearch = {
    username: profileOwner.user.username,
    type: streamParams.gallectionType ?? 'gallery',
    order: streamParams.sortOrder === GallectionSortOrder.NEWEST ? 'most-recent' : 'popular',
    q: streamParams.searchQuery,
    init: streamParams.mode === 'paged' || offset === 0, // TODO rename init to something else
    offset,
    limit,
  };

  if (!validateGallectionSearchRequest(queryParams)) {
    yield putErrorNotification(i18next.t('common.error.validationFailed'));
    yield put(withOffset.actions.fetchFailure({ streamId, fetchType: FetchType.Next }));
    return;
  }

  yield fetchStreamContents<PapiRequestDaSharedGallectionSearch, PapiDaSharedGallectionSearch>({
    streamId,
    url: '/gallection/search',
    params: queryParams,
    getStreamData: response => {
      const { result, entities } = normalize(response.results || [], [deviationSchema]);
      return {
        hasMore: response.hasMore,
        nextOffset: response.nextOffset,
        items: result,
        entities,
        total: response.estTotal,
      };
    },
  });
}
